import { useMemo } from 'react'

import type { IntlFormatters } from 'react-intl'
import { FormattedNumber, useIntl } from 'react-intl'
import { styled } from 'styled-components'

import { isZeroQuotePlaceholder } from '@b-stock/fe-core/bstock-next/helpers'
import { type OrchestratedQuote } from '@b-stock/fe-core/bstock-next/queries'
import {
  FormattedMessage,
  CurrencyStringDollars,
  CurrencyStringCents,
  centsToDollars,
  Tag,
  TagType,
  CurrencyPlaceholder,
} from '@b-stock/fe-core/bstock-react'
import { Colors, Typography } from '@b-stock/fe-core/design-system'
import type { Shipping } from '@b-stock/listing-api-client'
import type { PayMethod } from '@b-stock/payments-methods-api-client'
import { ShipmentType } from '@b-stock/shipment-api-client'

import type { CardOrderItem } from '@/buyer-portal/components/CardOrder'
import type { CalculatedAdditionalCharges } from '@/buyer-portal/components/features/listing/hooks/useCalculatedAdditionalCharges'
import {
  calculateConvenienceFee,
  calculateCreditCardConvenienceFee,
} from '@/buyer-portal/components/features/order/checkout/helpers/prepareCheckoutProps'
import { useCreditCardConvenienceFeeMultiplier } from '@/buyer-portal/components/features/order/checkout/helpers/useCreditCardConvenienceFeeMultiplier'
import { classifyConvenienceFeeFromMethod } from '@/buyer-portal/components/features/order/checkout/helpers/utils'
import {
  isFixedShipmentType,
  isGeofencedFlatRateQuote,
} from '@/buyer-portal/helpers/shipping'
import { calculateInsuranceEstimateCents } from '@/helpers/calculateInsuranceEstimate'
import { calculateShippingCostBreakdown } from '@/helpers/calculateShippingCostBreakdown'

// Matches TlcBreakdown's treatment of the same absence, so the card and the
// modal read the same way on the same lot.
export const UnavailableValue = styled.span`
  ${Typography.Body2}
  color: ${Colors.Semantic.Neutral.dark_grey};
`

const ns = 'TotalPaymentInfo'

export const getQuoteShipmentType = (
  selectedQuote: OrchestratedQuote | null | undefined,
  shipping: Shipping | null | undefined
) => {
  return (
    selectedQuote?.shipmentType ??
    (shipping?.allowedShipmentTypes?.[0] as ShipmentType | undefined) ??
    ShipmentType.Binding
  )
}

const getTotalPaymentInfo = ({
  intl,
  purchaseAmountDollars,
  selectedQuote,
  shipping,
  currency,
  additionalCharges,
  isOffer,
  isBuyNow,
  selectedPaymentMethod,
  isWireSelected,
  creditCardConvenienceFeeMultiplier,
  isCheapestCarrier,
  totalTaxCents,
  suppressShippingLine,
  isSellerCollects,
}: {
  intl: IntlFormatters
  purchaseAmountDollars: number
  selectedQuote?: OrchestratedQuote | null
  shipping: Shipping | null
  currency: string
  additionalCharges: CalculatedAdditionalCharges
  isOffer: boolean
  isBuyNow: boolean
  selectedPaymentMethod: PayMethod | null
  isWireSelected: boolean
  creditCardConvenienceFeeMultiplier: number
  isCheapestCarrier: boolean
  // FP-2498 — Canadian max-tax exposure folded into Estimated Total;
  // FP-3261 — EU/GB max-VAT exposure likewise. 0 for untaxed orders or
  // when the max-exposure lookup is pending / unavailable (mirrors the
  // tax card's own suppression).
  totalTaxCents: number
  /**
   * B-Stock collects no freight on this lot, so the modal has no figure to
   * show — distinct from `isZeroQuoteValue`, which means the quote is a
   * placeholder. A pickup quote of `totalPrice: 0` is a real quote, and
   * printing it as "$0.00" claims a cost B-Stock does not collect (FP-3356).
   */
  suppressShippingLine: boolean
  /**
   * The convenience fee is what the processor charges to move money to
   * B-Stock. On a Seller Collects lot the buyer pays the seller directly and
   * B-Stock handles none of the goods payment, so there is no processing to
   * charge for and no fee to show.
   */
  isSellerCollects: boolean
}): CardOrderItem[] => {
  const { formatMessage } = intl

  const isZeroQuoteValue = isZeroQuotePlaceholder(selectedQuote)
  const purchaseAmountKey = isOffer
    ? 'offerAmount'
    : isBuyNow
      ? 'buyNowAmount'
      : 'maxBid'

  const {
    estimatedShippingAmount,
    liftgateDeliveryFee,
    residentialDeliveryFee,
  } = calculateShippingCostBreakdown(selectedQuote)

  const shipmentType = getQuoteShipmentType(selectedQuote, shipping)
  const isFixedShipping = isFixedShipmentType(shipmentType)
  /**
   * A figure still to come, rather than free freight. Only a rated lot is
   * waiting for one: buyer-arranged, pickup, free and flat-rate lots never get
   * a quote, so a placeholder there would never resolve and would read as
   * pending for ever. The row above stays priced either way — on those lots
   * the total is complete.
   */
  const isQuotePending =
    isZeroQuoteValue || (!selectedQuote && !isFixedShipping)
  const isGeofencedFlatRate = isGeofencedFlatRateQuote(selectedQuote)

  // Mandatory parcel shipping-insurance estimate. Shipment adds the quote's
  // `insurance` subdoc only to USD parcel quotes for sellers with parcel
  // enabled, so the row needs no flag of its own. Hidden when the subdoc is
  // absent or `available` is false, and on the zero-quote placeholder.
  const insurance =
    !isZeroQuoteValue && selectedQuote?.insurance?.available === true
      ? selectedQuote.insurance
      : undefined
  const bidCents = purchaseAmountDollars * 100
  const insuranceEstimateCents = insurance
    ? calculateInsuranceEstimateCents(insurance, bidCents)
    : 0
  const insuredCents = insurance
    ? Math.min(bidCents, insurance.coverageLimit)
    : 0

  // Convenience fees are charged on the pre-tax subtotal — tax is a
  // pass-through to the CRA, not revenue the processor fee applies to. The
  // order charges the fee on the shipping insurance too, so it is in the base.
  const preTaxSubtotalCents =
    bidCents +
    (selectedQuote?.totalPrice ?? 0) +
    additionalCharges.bstockFee +
    insuranceEstimateCents

  const feeKind = classifyConvenienceFeeFromMethod(
    selectedPaymentMethod,
    isWireSelected
  )
  const convenienceFeeCents =
    isZeroQuoteValue || isSellerCollects
      ? 0
      : feeKind === 'creditCard'
        ? calculateCreditCardConvenienceFee(
            preTaxSubtotalCents,
            creditCardConvenienceFeeMultiplier
          )
        : feeKind === 'netTerms'
          ? calculateConvenienceFee(preTaxSubtotalCents)
          : 0
  const showConvenienceFeeRow = feeKind !== null && convenienceFeeCents > 0

  const convenienceFeeRow: CardOrderItem | null = showConvenienceFeeRow
    ? {
        title: formatMessage({ id: `${ns}.convenienceFee.${feeKind}` }),
        value: (
          <CurrencyStringCents
            value={convenienceFeeCents}
            currency={currency}
          />
        ),
      }
    : null

  const insuranceRow: CardOrderItem | null = insurance
    ? {
        title: formatMessage({ id: `${ns}.estimatedShippingInsurance` }),
        value: (
          <CurrencyStringCents
            value={insuranceEstimateCents}
            currency={currency}
          />
        ),
        // The disclosure shows on every estimate; the over-limit note only
        // when the bid is above the coverage limit.
        description: (
          <>
            <div>
              <FormattedMessage
                id={`${ns}.estimatedShippingInsuranceCoverage.${purchaseAmountKey}`}
                values={{
                  amount: (
                    <CurrencyStringCents
                      value={insuredCents}
                      currency={currency}
                      omitDecimal={insuredCents % 100 === 0}
                    />
                  ),
                  limit: (
                    <CurrencyStringCents
                      value={insurance.coverageLimit}
                      currency={currency}
                      omitDecimal
                    />
                  ),
                }}
              />
            </div>
            {insuredCents < bidCents ? (
              <div>
                <FormattedMessage
                  id={`${ns}.estimatedShippingInsuranceOverLimit`}
                  values={{
                    limit: (
                      <CurrencyStringCents
                        value={insurance.coverageLimit}
                        currency={currency}
                        omitDecimal
                      />
                    ),
                  }}
                />
              </div>
            ) : null}
            <div>
              <FormattedMessage
                id={`${ns}.estimatedShippingInsuranceDisclosure.${purchaseAmountKey}`}
                values={{
                  limit: (
                    <CurrencyStringCents
                      value={insurance.coverageLimit}
                      currency={currency}
                      omitDecimal
                    />
                  ),
                  rate: (
                    <FormattedNumber
                      value={insurance.ratePct / 100}
                      style="percent"
                      maximumFractionDigits={2}
                    />
                  ),
                  minimum: (
                    <CurrencyStringCents
                      value={insurance.minimumPremium ?? 0}
                      currency={currency}
                    />
                  ),
                }}
              />
            </div>
          </>
        ),
      }
    : null

  return [
    {
      title: formatMessage({ id: `${ns}.estimatedTotal` }),
      value: isZeroQuoteValue ? (
        <CurrencyPlaceholder currency={currency} />
      ) : (
        <CurrencyStringDollars
          value={centsToDollars(
            preTaxSubtotalCents + convenienceFeeCents + totalTaxCents
          )}
          currency={currency}
        />
      ),
      isValueWarning: isZeroQuoteValue,
      description: isBuyNow
        ? formatMessage({ id: `${ns}.buyNowTotalDescription` })
        : formatMessage({ id: `${ns}.estimatedTotalDescription` }),
      children: [
        {
          title: formatMessage({ id: `${ns}.${purchaseAmountKey}` }),
          value: (
            <CurrencyStringDollars
              value={purchaseAmountDollars}
              currency={currency}
            />
          ),
        },
        {
          title: formatMessage({ id: `${ns}.bstockFee` }),
          value: (
            <CurrencyStringCents
              value={additionalCharges.bstockFee}
              currency={currency}
            />
          ),
        },
        ...(convenienceFeeRow !== null ? [convenienceFeeRow] : []),
        ...(insuranceRow !== null ? [insuranceRow] : []),
        {
          title: formatMessage({ id: `${ns}.shippingTotal` }),
          tag: isFixedShipping ? (
            <Tag type={TagType.info}>
              <FormattedMessage
                id={`${ns}.shippingTags.${isGeofencedFlatRate ? 'LOCAL_FLAT_RATE' : shipmentType}`}
              />
            </Tag>
          ) : undefined,
          valueTag:
            !isFixedShipping && isCheapestCarrier ? (
              <Tag type={TagType.normal}>
                <FormattedMessage id="TotalPaymentInfo.cheapest" />
              </Tag>
            ) : undefined,
          value: suppressShippingLine ? (
            <UnavailableValue>—</UnavailableValue>
          ) : isQuotePending ? (
            <CurrencyPlaceholder currency={currency} />
          ) : (
            <CurrencyStringCents
              value={selectedQuote?.totalPrice ?? 0}
              currency={currency}
            />
          ),
          isValueWarning: isQuotePending,
          description:
            isFixedShipping || isQuotePending || suppressShippingLine
              ? undefined
              : formatMessage({ id: `${ns}.shippingTotalDescription` }),
          // The children only ever break the amount down. With no amount to
          // assert there is nothing to break down, so the row stands alone
          // rather than expanding into a list of zeroes.
          children:
            isFixedShipping || isQuotePending || suppressShippingLine
              ? undefined
              : [
                  {
                    title: formatMessage(
                      {
                        id:
                          selectedQuote?.carrier?.name &&
                          selectedQuote.estimatedDeliveryDuration?.businessDays
                            ? `${ns}.estimatedShippingAmount`
                            : `${ns}.estimatedShippingAmountDefault`,
                      },
                      {
                        carrier: selectedQuote?.carrier?.name,
                        days:
                          selectedQuote?.estimatedDeliveryDuration
                            ?.businessDays ?? 'N/A',
                      }
                    ),
                    value: (
                      <CurrencyStringCents
                        value={estimatedShippingAmount}
                        currency={currency}
                      />
                    ),
                  },
                  {
                    title: formatMessage({ id: `${ns}.liftgateFee` }),
                    value: (
                      <CurrencyStringCents
                        value={liftgateDeliveryFee ?? 0}
                        currency={currency}
                      />
                    ),
                  },
                  {
                    title: formatMessage({ id: `${ns}.residentialFee` }),
                    value: (
                      <CurrencyStringCents
                        value={residentialDeliveryFee ?? 0}
                        currency={currency}
                      />
                    ),
                  },
                ],
        },
      ],
    },
  ]
}

export const useTotalPaymentInfo = ({
  purchaseAmountDollars,
  selectedQuote,
  shipping,
  currency = 'USD',
  additionalCharges,
  isOffer,
  isBuyNow = false,
  selectedPaymentMethod = null,
  isWireSelected = false,
  isCheapestCarrier = false,
  totalTaxCents = 0,
  suppressShippingLine = false,
  isSellerCollects = false,
}: {
  purchaseAmountDollars: number
  selectedQuote?: OrchestratedQuote | null
  shipping: Shipping | null
  currency?: string
  additionalCharges: CalculatedAdditionalCharges
  isOffer: boolean
  isBuyNow?: boolean
  selectedPaymentMethod?: PayMethod | null
  isWireSelected?: boolean
  isCheapestCarrier?: boolean
  totalTaxCents?: number
  /**
   * B-Stock collects no freight on this lot, so the modal has no figure to
   * show — distinct from `isZeroQuoteValue`, which means the quote is a
   * placeholder. A pickup quote of `totalPrice: 0` is a real quote, and
   * printing it as "$0.00" claims a cost B-Stock does not collect (FP-3356).
   */
  suppressShippingLine?: boolean
  /** True on a Seller Collects lot; suppresses the convenience fee. */
  isSellerCollects?: boolean
}) => {
  const intl = useIntl()
  const creditCardConvenienceFeeMultiplier =
    useCreditCardConvenienceFeeMultiplier()

  return useMemo(
    () =>
      getTotalPaymentInfo({
        intl,
        purchaseAmountDollars,
        selectedQuote,
        shipping,
        currency,
        additionalCharges,
        isOffer,
        isBuyNow,
        selectedPaymentMethod,
        isWireSelected,
        creditCardConvenienceFeeMultiplier,
        isCheapestCarrier,
        totalTaxCents,
        suppressShippingLine,
        isSellerCollects,
      }),
    [
      intl,
      purchaseAmountDollars,
      selectedQuote,
      shipping,
      currency,
      additionalCharges,
      isOffer,
      isBuyNow,
      selectedPaymentMethod,
      isWireSelected,
      creditCardConvenienceFeeMultiplier,
      isCheapestCarrier,
      totalTaxCents,
      suppressShippingLine,
      isSellerCollects,
    ]
  )
}
